import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGenerate } from '../../../context/GenerateContext'
import { useProviders } from '../../../hooks/useProviders'

/**
 * Список разделов нижнего бара виджета и переход между ними.
 *
 * Отдельно от компонента (WidgetSectionNav) по требованию react-refresh:
 * файл с компонентом не должен экспортировать ещё и константы/хуки.
 */

export interface SectionMode {
	category: string
	labelKey: string
	icon: string
	/** Цветная 3D-иконка: CSS снимает с неё монохромные opacity/filter. */
	colored?: boolean
}

// «Тренды» и «Презентации» — не категории генерации, а РАЗДЕЛЫ: своих моделей
// у них нет, клик просто уводит по маршруту (ветка route в switchTo
// отрабатывает раньше подбора модели). Порядок — как в сайдбаре.
// ?v=2 — cache-bust иконки (public/ не хэшируется, Cache-Control 30д).
export const SECTION_MODES: SectionMode[] = [
	{ category: 'trends', labelKey: 'nav.bots.trends', icon: '/icons/nav_trends.svg?v=2', colored: true },
	{ category: 'image', labelKey: 'generate.widget.photo', icon: '/icons/nav_img.svg' },
	{ category: 'video', labelKey: 'generate.widget.videoMode', icon: '/icons/nav_video.svg' },
	{ category: 'audio', labelKey: 'generate.widget.audioMode', icon: '/icons/nav_audio.svg' },
	{ category: 'slides', labelKey: 'nav.bots.slides', icon: '/icons/nav_slides.svg' },
]

const CATEGORY_ROUTES: Record<string, string> = {
	trends: '/bots/trends',
	image: '/bots/images',
	video: '/bots/video',
	// «Музыка» переименована в «Популярное» и переехала на root /bots/audio
	// (выравнивание с images/video). Юзер при переключении audio-режима
	// попадает на root который теперь = «Популярное» (music generation list).
	audio: '/bots/audio',
	// Презентации переехали из своего таба в «Инструменты» (см. App.tsx,
	// /bots/slides редиректит сюда же).
	slides: '/bots/tools/slides',
}

/** Раздел по текущему URL; вне /bots — категория выбранной модели. */
export function useSectionNav() {
	const location = useLocation()
	const navigate = useNavigate()
	const gen = useGenerate()
	const { widgetModels } = useProviders()

	const p = location.pathname
	const currentCategory = p.startsWith('/bots/trends')
		? 'trends'
		: p.startsWith('/bots/images')
			? 'image'
			: p.startsWith('/bots/video')
				? 'video'
				: p.startsWith('/bots/audio')
					? 'audio'
					: p.startsWith('/bots/tools/slides') || p.startsWith('/bots/slides')
						? 'slides'
						: gen.selectedModel?.category

	// Фолбэк — именно 'image' (а не первый элемент): список начинается с
	// «Трендов», и на чужом маршруте кнопка показывала бы огонь вместо фото.
	const currentMode =
		SECTION_MODES.find((m) => m.category === currentCategory) ??
		SECTION_MODES.find((m) => m.category === 'image')!

	const switchTo = useCallback(
		(category: string) => {
			const route = CATEGORY_ROUTES[category]
			if (route) {
				navigate(route)
				return
			}
			const model = widgetModels.find((m) => m.category === category)
			if (model && model.id !== gen.selectedModel?.id) {
				gen.setSelectedModel(model)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[navigate, widgetModels, gen.setSelectedModel, gen.selectedModel?.id],
	)

	return { currentCategory, currentMode, switchTo }
}

